'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { useI18n } from '@/components/public/i18n/LocaleProvider';
import { Arrow } from './Icons';

const JOURNEY = 'M -100 660 C 200 660 250 280 670 300 C 1080 320 1400 700 1110 740 C 820 780 560 420 850 255 C 1050 140 1420 190 1700 80';

export default function PortalDigitalIntro({ onFinished }: { onFinished: () => void }) {
  const { t } = useI18n();
  const dialog = useRef<HTMLDialogElement>(null);
  const lightMotion = useRef<SVGAnimateMotionElement>(null);
  const closing = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [ready, setReady] = useState(false);
  const [ending, setEnding] = useState(false);
  const finish = useCallback(() => {
    if (closing.current) return;
    closing.current = true;
    setEnding(true);
    timer.current = setTimeout(() => {
      dialog.current?.close();
      onFinished();
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 420);
  }, [onFinished]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.showModal();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => { if (motion.matches) finish(); };
    motion.addEventListener('change', update);
    update();
    return () => {
      if (timer.current) clearTimeout(timer.current);
      motion.removeEventListener('change', update);
      document.body.style.overflow = previous;
    };
  }, [finish]);

  useEffect(() => {
    if (!ready) return;
    // Start the logo zoom, light journey and closing timer together after loading.
    lightMotion.current?.beginElement();
    const timeout = setTimeout(finish, 2800);
    return () => clearTimeout(timeout);
  }, [ready, finish]);

  return (
    <dialog ref={dialog} className={`u-intro-dialog u-digital-intro${ready ? ' is-ready' : ''}${ending ? ' is-ending' : ''}`}
      aria-label={t('Introduction RDV-Partners')}
      onCancel={(event) => { event.preventDefault(); finish(); }}>
      <div className="u-digital-atmosphere" aria-hidden="true" />
      <svg className="u-digital-journey" viewBox="0 0 1600 900" preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <defs>
          <filter id="intro-halo" x="-50%" y="-100%" width="200%" height="300%"><feGaussianBlur stdDeviation="12" /></filter>
          <filter id="intro-glow" x="-50%" y="-100%" width="200%" height="300%"><feGaussianBlur stdDeviation="3" /></filter>
        </defs>
        <path className="u-digital-trail u-digital-trail-halo" d={JOURNEY} pathLength="1" filter="url(#intro-halo)" />
        <path className="u-digital-trail u-digital-trail-glow" d={JOURNEY} pathLength="1" filter="url(#intro-glow)" />
        <path className="u-digital-trail u-digital-trail-core" d={JOURNEY} pathLength="1" />
        <g className="u-digital-point">
          <circle r="22" fill="#73e9cf" opacity=".65" filter="url(#intro-halo)" />
          <circle r="7" fill="#b6ffe8" filter="url(#intro-glow)" />
          <circle r="3" fill="#f7fff3" />
          <animateMotion ref={lightMotion} begin="indefinite" dur="2.8s" path={JOURNEY} calcMode="spline" keyTimes="0;1" keySplines=".4 0 .2 1" keyPoints="0;1" fill="freeze" />
        </g>
      </svg>
      <div className="u-digital-brand">
        <div className="u-digital-logo">
          <Image src="/assets/universes/rdv-partners-intro-logo-20261015.png" alt="RDV Partners" width={1024} height={1024}
            sizes="(max-width: 760px) 122vw, 67vw" quality={85} loading="eager"
            fetchPriority="high" decoding="async" draggable={false} onLoad={() => setReady(true)} onError={() => setReady(true)} />
        </div>
        <small>{t('Votre ambition. Notre engagement.')}</small>
      </div>
      <div className="u-intro-controls"><button autoFocus onClick={finish} type="button">{t('Passer l’introduction ')}<Arrow /></button></div>
    </dialog>
  );
}

